import {create} from 'zustand'
import {ExportJob} from '@/features/exports/types'

/**
 * Holds the export job currently announced in the popover anchored to the exports toggle
 * (replacing the transient toast). The popover renders its content from the job's `status`:
 * `pending`/`processing` → "in elaborazione", `ready` → download CTA, `failed` → failure reason.
 *
 * Set by the enqueue flow (after the dialog "flies" into the toggle, as a pending job) and by the
 * socket listener when the job turns ready/failed; cleared when the user dismisses it (X),
 * downloads, or opens the drawer.
 */
/** Ready/failed popover phrases for a single job, overriding the generic export wording. */
export interface ExportAnnouncementTitleOverride {
    ready: string
    failed: string
}

interface ExportAnnouncementState {
    job: ExportJob | null
    /**
     * Per-job title overrides, keyed by job id. The generic `ExportJob` carries no domain context
     * (e.g. an activity report's period + sector), so the enqueue flow can register a ready/failed
     * phrase here; it survives the pending → ready/failed transition the socket drives. Absent for
     * generic exports, which keep the default "{entity} - Esportazione pronta/fallita" wording.
     */
    titleOverrides: Record<string, ExportAnnouncementTitleOverride>
    announce: (job: ExportJob) => void
    registerTitleOverride: (jobId: string, override: ExportAnnouncementTitleOverride) => void
    dismiss: () => void
}

export const useExportAnnouncementStore = create<ExportAnnouncementState>(set => ({
    job: null,
    titleOverrides: {},
    announce: job => set({job}),
    registerTitleOverride: (jobId, override) =>
        set(state => ({titleOverrides: {...state.titleOverrides, [jobId]: override}})),
    dismiss: () => set({job: null})
}))
